<template>
  <div class="page-channels" id="page-channels">
    <div class="mb-4">
      <Breadcrumb :items=breadCrumbs() v-if="loaded" />
    </div>
    <div>
      <tabs :items="tabs" v-model="tab" :with-content="true" @switch="switchTab">
        <template v-for="(languageTab,index) in tabs" v-slot:[tabName(languageTab)]>
          <div class="list d-none d-md-block left-edge float-right mb-3" :key="index">
            <p class="head-line">
              <check-box :disabled="pending" v-model="withoutTranslations" @input="displayOnlyWithoutTranslations">
                <span class="ft-14" data-toggle="tooltip"
                      :title="$t('pages.project-translations.filters.without-translations')"
                >
                  {{ $t('pages.project-translations.filters.without-translations') }}
                </span>
              </check-box>
              <search-filter
                v-model="filter"
                :disabled="!loaded || pending"
                :placeholder="$t('pages.project-page.filter-tip')"
                @input="onSearch()"
                class="ml-3"
              />
            </p>
          </div>
          <div class="placeHolderString" :key="`placeholder-${index}`">
            <p v-if="languageTab.iso === 'en'" class="txt-red">
              {{ $t('pages.project-key.rules.placeholder-variable') }}
              <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
            </p>
            <p v-else class="txt-red mb-2">
              {{ $t('pages.project-key.rules.placeholder-variable-not-english') }}
              <span class="bold txt-bold">{{ $t('pages.project-key.rules.placeholder-variable-eg') }}</span>
            </p>
            <p class="txt-red">
              {{ $t('pages.project-key.rules.helpful-tools') }}
              <span class="bold txt-bold">
                <a class="txt-red" target="_blank" href="https://onlinehtmleditor.dev/">
                  {{ $t('pages.project-key.rules.html-editor') }}
                </a>
                <a class="txt-red" target="_blank" href="https://webformatter.com/html">
                  {{ $t('pages.project-key.rules.html-formatter') }}
                </a>
                <a class="txt-red" target="_blank" href="https://html.onlineviewer.net/">
                  {{ $t('pages.project-key.rules.html-viewer') }}
                </a>
              </span>
            </p>
          </div>
          <div class="plans-table" v-if="loaded" :key="index+languageTab">
            <table class="w-100">
              <thead>
                <tr>
                  <th class="w-name">{{ $t('pages.project-translations.headers.key') }}</th>
                  <th class="w-name text-center">{{ $t('pages.project-translations.headers.description') }}</th>
                  <th class="w-name">{{ $t('pages.project-translations.headers.translation') }}</th>
                  <th class="w-name">Automated</th>
                </tr>
              </thead>
              <tbody v-if="loaded && filterProjectKeys.length === 0">
                <tr>
                  <td colspan="4" class="w-empty">{{ $t('pages.project-key.no-project-key') }}</td>
                </tr>
              </tbody>
              <tbody v-for="row in filterProjectKeys" :key="row.keywordKey">
                <tr class="separator before"></tr>
                <tr>
                  <td>
                    ${{ row.keywordKey }}
                  </td>
                  <td class="text-center">
                    <span>{{ descriptionLimited(row.keywordDescription) }} </span>
                    <icon
                      class="icon mr-1 mb-1"
                      width="12" height="12"
                      type="info-circle"
                      v-b-tooltip:page-channels.bottom="row.keywordDescription"
                      v-if="row.keywordDescription.length > 20"
                    />
                  </td>
                  <td class="actions">
                    <p
                      style="text-align: left;"
                      v-bind:style="{color: row.translation.translation ? 'black' : 'red'}">
                      {{ row.englishTranslation }}
                    </p>
                    <ValidationObserver ref="form-translation">
                      <ValidatedField
                        class="float-left w-85"
                        :disabled="updatePending"
                        :rules="rulesForRichTextEditor"
                        :type="row.richtextField ? 'richtext': 'textarea'"
                        v-model="row.translation.translation"
                        :placeholder="$t('pages.project-translations.placeholders.add-translation')"
                        :name="`translation-${row.keywordId}`"
                      />

                      <!-- <div class="float-left w-85 ml-1 mb-3">
                        <check-box v-model="row.richtextField" @click="checkboxStatus(row)">
                          <span>Rich Text Editor</span>
                        </check-box>
                      </div> -->

                      <div class="float-left w-85">
                        <SubmitButton
                          @click="onSubmit(row)"
                          variant="secondary"
                          class="btn-save ml-1"
                          :disabled="pending && !loaded || updatePending"
                          :loading="pending"
                          inline
                        >
                          {{ $t('buttons.save') }}
                        </SubmitButton>
                        <template v-if="row.translation.is_automated === 3">
                          <button class="btn btn-success m-1"
                                  @click="callDeepLApi(row.englishTranslation, languageTab.iso, row)" >
                            Translate with DeepL
                          </button>
                        </template>
                      </div>
                    </ValidationObserver>
                  </td>

                  <template>
                    <td v-if="row.translation.translation === '' ">
                      <span>-</span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 0">
                      <span>
                        <p>Manual</p>
                        <!-- <p class="dateFont">{{"("+ row.translation.created_at +")"}}</p> -->
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 1">
                      <span>
                        <p>Fetched from DeepL</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 2">
                      <span>
                        <p>Changed after DeepL</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}}</p></i>
                      </span>
                    </td>
                    <td v-else-if="row.translation.is_automated === 3">
                      <span>
                        <p>To be checked</p>
                        <b>Last Updated: </b>
                        <i><p class="dateFont">{{"("+ row.translation.updated_at +")"}} </p></i>
                      </span>
                    </td>
                    <td v-else>
                      <span>-</span>
                    </td>
                  </template>

                </tr>
                <tr class="separator after"></tr>
              </tbody>
            </table>
          </div>
          <pagination
            :key="languageTab+index"
            v-if="loaded && totalCount && setPagination"
            :perPage="20"
            :currentPage="currentPage"
            :totalRows="totalCount"
            @update:current-page="updateCurrentPage($event)"
          />
        </template>
      </tabs>

    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions } from 'vuex';


  export default {
    name: 'ProjectPageTranslations',
    data: () => ({
      tabs: [],
      activeTab: null,
      currentPage: 1,
      disabledSubmit: false,
      filter: '',
      withoutTranslations: false,
      setPagination: true,
    }),
    async created() {
      if (!this.isAdmin) {
        this.tabs.push({ id: this.user.language.id, title: this.user.language.name, iso: this.user.language.iso });
        this.activeTab = this.user.language.id;
      } else {
        await this.fetchLanguages();
        this.languages.forEach((language) => {
          if (!this.activeTab) {
            this.activeTab = language.id;
          }
          this.tabs.push({ id: language.id, title: language.name, iso: language.iso });
        });
      }
      await this.show();
    },
    computed: {
      ...mapGetters('user', ['user', 'isAdmin']),
      ...mapGetters('languages', ['languages']),
      ...mapGetters('projectTranslations', ['loaded', 'noProjectKeys', 'projectKeys']),
      ...mapState('projectTranslations', ['error', 'pending', 'updatePending', 'totalCount', 'projectPage', 'project']),
      filterProjectKeys() {
        return this.projectKeys;
      },
      tab: {
        get() { return this.activeTab; },
        set(tab) {
          this.activeTab = tab;
        },
      },
      rulesForRichTextEditor() {
        const rules = {};
        rules.required = true;
        return rules;
      },
    },
    methods: {
      ...mapActions('projectTranslations', ['fetchData', 'createProjectTranslation', 'updateProjectTranslation']),
      ...mapActions('languages', { fetchLanguages: 'fetchData' }),
      ...mapActions('deeplTranslations', ['createDeeplTranslation']),

      /** **************************************************
       *
       *  calling api to fetch single translation
       *
       ***************************************************  */
      async callDeepLApi(textString, langCode, row) {
        try {
          const payload = {
            text: textString,
            target_lang: langCode,
            language_id: this.activeTab,
            keyword_id: row.keywordId,
            keywords_translated: row.translation.keywordTranslatedId,
          };
          // Translate and Save keyword into the DB
          await this.createDeeplTranslation(payload);
          await this.show();
          this.$toastr.s({
            title: this.$t('pages.project-translations.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
        } catch (error) {
          this.$toastr.e(error);
        }
      },
      checkboxStatus(row) {
        this.filterProjectKeys = this.filterProjectKeys.map((item) => {
          if (item.keywordId === row.keywordId) {
            const newItem = { ...item };
            newItem.richtextField = !row.richtextField;
            return newItem;
          }
          return item;
        });
      },
      async show() {
        if (this.withoutTranslations) {
          this.setPagination = false;
        }
        await this.fetchData({
          currentPage: this.currentPage,
          projectPageId: this.$route.params.id,
          languageId: this.activeTab,
          q: this.filter,
          withoutTranslations: this.withoutTranslations,
        });
      },
      async switchTab() {
        await this.show();
      },
      tabName(tab) {
        return `tab(${tab.id})`;
      },
      async onSubmit(row) {
        if (row.translation.translation !== '') {
          const payload = {
            translation: row.translation.translation,
            language_id: this.activeTab,
            keyword_id: row.keywordId,
            keywords_translated: row.translation.keywordTranslatedId,
          };
          await this.createProjectTranslation(payload);
          this.$toastr.s({
            title: this.$t('pages.project-translations.alert-saved'),
            msg: '',
            timeout: 3000,
            progressbar: false,
          });
        }
      },
      updateCurrentPage(currentPage) {
        this.currentPage = currentPage;
        this.show();
      },
      breadCrumbs() {
        return [
          {
            text: this.$t('pages.dashboard.title'),
            to: { name: 'dashboard' },
          },
          {
            text: this.project.name,
            to: { name: 'projects' },
          },
          {
            text: this.projectPage.name,
            to: { name: 'projects-pages', params: { id: this.project.id } },
          },
          {
            text: this.$t('pages.project-translations.title'),
            active: true,
          },
        ];
      },
      async onSearch() {
        if (this.filter.length === 0 || this.filter.length >= 3) {
          if (this.filter.length >= 3) {
            this.setPagination = false;
          } else {
            this.setPagination = true;
          }
          await this.show();
        }
      },
      async displayOnlyWithoutTranslations() {
        await this.show();
      },
      descriptionLimited(description) {
        if (description.length > 20) {
          return `${description.substring(0, 20) }...`;
        }
        return description;
      },
    },
  };
</script>

<style scoped>
.dateFont {
  font-size: 12px;
}
.placeHolderString {
  margin-top: 63px;
}
</style>
